import { useState, useCallback } from "react";
import { useDebugMode } from "./useDebugMode";
import {
  PMUIWorker,
  PMGradingRow,
  RetrieveConfigurationResult,
  CC2MasterSheetDataRow,
  ProductionFormState,
} from "../pages/pm-grading/types";
import {
  ProductionFormStateWithFrontEndOnlyFields,
  toProductionData,
} from "../pages/pm-grading/utilities";

/**
 * Custom hook for handling data operations in PM Grading
 */
export const usePMGradingOperations = (
  pmuiWorker: PMUIWorker,
  authToken: string | undefined,
  pmuiConfiguration: RetrieveConfigurationResult,
  updatePMRow: (entityId: string, rowData: PMGradingRow) => void,
  initializeFormData: (
    entityId: string,
    formData: Partial<ProductionFormStateWithFrontEndOnlyFields>
  ) => void
) => {
  const [currentCC2, setCurrentCC2] = useState<CC2MasterSheetDataRow>();
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [alertState, setAlertState] = useState({
    show: false,
    message: "",
    variant: "success" as "success" | "danger" | "info" | "warning",
  });

  const debugMode = useDebugMode();

  // Show alert message
  const showAlert = useCallback(
    (
      message: string,
      variant: "success" | "danger" | "info" | "warning" = "success",
      timeout: number = 10000
    ) => {
      window.scrollTo({
        behavior: "smooth",
        top: 60, // Approximate height of the header + a bit of padding
      });
      setAlertState({
        show: true,
        message,
        variant,
      });

      setTimeout(() => {
        hideAlert();
      }, timeout);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Hide alert message
  const hideAlert = useCallback(() => {
    setAlertState((prev) => ({
      ...prev,
      show: false,
    }));
  }, []);

  // Find CC2 data for a given entity ID
  const findCC2ForEntity = useCallback(
    (entityId: string): CC2MasterSheetDataRow | undefined => {
      if (!pmuiConfiguration?.cc2Data) return undefined;

      return pmuiConfiguration.cc2Data.find((cc2) =>
        entityId.startsWith(cc2.skuSuffix)
      );
    },
    [pmuiConfiguration]
  );

  const clearCC2ForEntity = useCallback(() => {
    setCurrentCC2(undefined);
  }, []);

  // Fetch a single PM row by entity ID
  const fetchPMRow = useCallback(
    async (
      entityId: string,
      skipDatabase = false
    ): Promise<PMGradingRow | null> => {
      if (!authToken) return null;

      try {
        if (debugMode) {
          console.log("[usePMGradingOperations] Fetching data for:", entityId);
        }

        setLoading((prev) => ({ ...prev, [entityId]: true }));

        const cc2Data = findCC2ForEntity(entityId);

        if (!cc2Data) {
          throw new Error(`No matching CC2 data found for PMID: ${entityId}`);
        }

        setCurrentCC2(cc2Data);

        const pmSheetId = cc2Data.pmSpreadsheetId;
        if (!pmSheetId) {
          throw new Error(`No PM Sheet ID found for client: ${cc2Data.client}`);
        }

        const rowData = await pmuiWorker.retrievePMRow(
          authToken,
          entityId,
          pmSheetId,
          skipDatabase
        );

        if (rowData) {
          if (rowData.pmGradingRow) {
            updatePMRow(entityId, rowData.pmGradingRow);
          }
          return rowData.pmGradingRow || null;
        } else {
          throw new Error(`No data found for PMID: ${entityId}`);
        }
      } catch (error) {
        showAlert(
          `Error fetching data: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          "danger"
        );
        return null;
      } finally {
        setLoading((prev) => ({ ...prev, [entityId]: false }));
      }
    },
    [authToken, debugMode, findCC2ForEntity, pmuiWorker, showAlert, updatePMRow]
  );

  // Update an existing PM row
  const submitUpdatePMRow = useCallback(
    async (
      entityId: string,
      formData: Partial<ProductionFormState>,
      existingRowData: PMGradingRow
    ): Promise<boolean> => {
      if (!authToken) return false;

      try {
        if (debugMode) {
          console.log(
            "[usePMGradingOperations] Updating data for:",
            entityId,
            formData
          );
        }

        setLoading((prev) => ({ ...prev, [entityId]: true }));

        const cc2Data = findCC2ForEntity(entityId);

        if (!cc2Data) {
          throw new Error(`No matching CC2 data found for PMID: ${entityId}`);
        }

        const pmSheetId = cc2Data.pmSpreadsheetId;
        if (!pmSheetId) {
          throw new Error(`No PM Sheet ID found for client: ${cc2Data.client}`);
        }

        // Convert the form data to the format expected by the worker
        const productionData = toProductionData(formData);
        // Update the existing row data with the new production data
        const updatedRowData = {
          ...existingRowData,
          outbound: {
            ...existingRowData.outbound,
            palletNumber:
              formData.refurbPalletNumber ??
              existingRowData.outbound.palletNumber ??
              "",
          },
          production: {
            ...existingRowData.production,
            // Ensure no undefined values by providing default empty strings and convert to proper type
            ...Object.entries(productionData).reduce((acc, [key, value]) => {
              // Skip manifestPalletNumber as it doesn't belong in the production section
              if (key === "manifestPalletNumber") return acc;
              acc[key] = value === undefined ? "" : String(value); // Convert to string and handle undefined
              return acc;
            }, {} as Record<string, string>),
          },
          manifest: {
            ...existingRowData.manifest,
            clientCode:
              productionData.clientCode ??
              existingRowData.manifest.clientCode ??
              "",
          },
        } as PMGradingRow;

        // Send the update to the worker
        const success = await pmuiWorker.putPMRow(
          authToken,
          entityId,
          pmSheetId,
          updatedRowData
        );

        if (success) {
          showAlert("Successfully updated data", "success");
          return true;
        } else {
          throw new Error("Failed to update data");
        }
      } catch (error) {
        showAlert(
          `Error updating data: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          "danger"
        );
        return false;
      } finally {
        setLoading((prev) => ({ ...prev, [entityId]: false }));
      }
    },
    [authToken, debugMode, findCC2ForEntity, pmuiWorker, showAlert]
  );

  return {
    currentCC2,
    loading,
    alertState,
    showAlert,
    hideAlert,
    fetchPMRow,
    submitUpdatePMRow,
    findCC2ForEntity,
    clearCC2ForEntity,
  };
};
